import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {MvsCrudService} from "features/core/shared/service/crud/mvs-crud.service";
import {ProvisionContractAgentDto} from "../../model/dto/entity/provision-contract-agent.dto";
import {ProvisionContractAgentSource} from "../../enum/provision-contract-agent-source.enum";
import {
    ProvisionAgentChangeConsequenceDto,
    ProvisionAgentChangeRequestDto,
    ProvisionAgentChangeResultDto
} from "../../model/dto/api/provision-agent-change.dto";
import {
    ProvisionAmountOverwriteRequestDto,
    ProvisionAmountOverwriteResultDto
} from "../../model/dto/api/provision-amount-overwrite.dto";

/**
 * Custom API service for cp.ProvisionContractAgent action endpoints (04-frontend.md §7, WP-E13).
 * REPLACES CpProvisionContractService: changeAgent moved off ProvisionContract onto
 * ProvisionContractAgent when the entity was renamed/restructured in WP-E5 (A6-04) — the write
 * target `{id}` is now the R-08 row (Vertrag × Ereignis × Provisionsart), not the R-07 contract.
 *
 * Base URL `/mvsa/cp/provisionContractAgents` — identical to the route the alias
 * `cp.ProvisionContractAgent` carries in cp.entity-provider.ts.
 *
 * Three action dialogs of 04-frontend.md §7, all under um right `cp_agentChange` (AX-08 — no new
 * rights introduced): "Betrag überschreiben" (overwriteAmount), "Berater wechseln" (changeAgent,
 * preview-first like its predecessor), "Satz manuell anlegen" (createManual, forces
 * source = MANUAL). NO handwritten form HTML — each dialog builds an `MvsFormDto` and hands it to
 * a TRANSIENT FORM WIDGET, the mechanism documented on CpAgentTransactionService.
 *
 * A business failure (e.g. threshold exceeded requiring extra confirmation) arrives as HTTP 200
 * with success = false (bm BmContractFrequencyChangeResultDto convention) — callers MUST read
 * `success`, never assume 2xx means applied.
 */
@Injectable({
    providedIn: 'root'
})
export class CpProvisionContractAgentService extends MvsCrudService {

    constructor(protected override http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/cp/provisionContractAgents');
    }

    /**
     * POST /{id}/overwriteAmount — sets provisionAmountOverwrite with overwriteSource = MANUAL
     * (E6/A6-11); the engine never touches it again on recalculation. Requires um right
     * `cp_agentChange`.
     */
    public overwriteAmount(provisionContractAgentId: number, request: ProvisionAmountOverwriteRequestDto): Observable<ProvisionAmountOverwriteResultDto> {
        return this.http.post<ProvisionAmountOverwriteResultDto>(
            this.apiUrl + `/${provisionContractAgentId}/overwriteAmount`, request
        );
    }

    /**
     * GET /{id}/changeAgent/preview — read-only consequence preview shown BEFORE the user
     * confirms the agent change (E8: which active bookings would be reversed/rebuilt).
     */
    public previewChangeAgent(provisionContractAgentId: number, newProvisionAgentId: number): Observable<ProvisionAgentChangeConsequenceDto[]> {
        return this.http.get<ProvisionAgentChangeConsequenceDto[]>(
            this.apiUrl + `/${provisionContractAgentId}/changeAgent/preview`,
            {params: {newProvisionAgentId}}
        );
    }

    /**
     * POST /{id}/changeAgent — applies the agent change (mandatory reason); sets
     * agentManuallyChanged = true. Requires um right `cp_agentChange`. A business failure arrives
     * as HTTP 200 with success = false.
     */
    public changeAgent(provisionContractAgentId: number, request: ProvisionAgentChangeRequestDto): Observable<ProvisionAgentChangeResultDto> {
        return this.http.post<ProvisionAgentChangeResultDto>(
            this.apiUrl + `/${provisionContractAgentId}/changeAgent`, request
        );
    }

    /**
     * POST / — creates a ProvisionContractAgent row by hand ("Satz manuell anlegen", 04-frontend.md
     * §7). Forces source = MANUAL regardless of what the caller set on the DTO, so the row is
     * never touched by a later recalculation (E6/A6-11). Caller supplies event line, provision
     * component and agent.
     */
    public createManual(dto: ProvisionContractAgentDto): Observable<ProvisionContractAgentDto> {
        dto.source = ProvisionContractAgentSource.MANUAL;
        return this.create(dto) as Observable<ProvisionContractAgentDto>;
    }

}
